import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type Phaser from 'phaser';
import { DEMO_LEVELS, type TrainLevel } from './domain/levelProgression';
import {
  EventBus,
  TRAIN_GAME_FINISHED_EVENT,
  TRAIN_HUD_UPDATE_EVENT,
  type TrainGameResult,
  type TrainHudUpdate,
} from './EventBus';
import { startGame } from './main';
import type { TrainHudState } from './scenes/TrainAdventureScene';
import { FullscreenGameShell } from './FullscreenGameShell';

type PhaserGameProps = {
  levels?: TrainLevel[];
  label?: string;
  restartEnabled?: boolean;
  onFinished?: (result: TrainGameResult['result']) => void;
};

export function PhaserGame({
  levels,
  label = 'Tren Macerası oyun tuvali',
  restartEnabled = true,
  onFinished,
}: PhaserGameProps) {
  const gameRef = useRef<Phaser.Game | null>(null);
  const onFinishedRef = useRef(onFinished);
  const gameId = `train-game-${useId().replace(/:/g, '')}`;
  const containerId = `${gameId}-container`;
  const gameLevels = levels ?? DEMO_LEVELS;
  const [hud, setHud] = useState<TrainHudState>(() => initialHud(gameLevels));

  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);

  useLayoutEffect(() => {
    const handleHudUpdate = ({ gameId: sourceGameId, hud: nextHud }: TrainHudUpdate) => {
      if (sourceGameId === gameId) setHud(nextHud);
    };
    const handleFinished = ({ gameId: sourceGameId, result }: TrainGameResult) => {
      if (sourceGameId === gameId) onFinishedRef.current?.(result);
    };
    EventBus.on(TRAIN_HUD_UPDATE_EVENT, handleHudUpdate);
    EventBus.on(TRAIN_GAME_FINISHED_EVENT, handleFinished);
    if (!gameRef.current) gameRef.current = startGame(containerId, gameId, gameLevels, restartEnabled);
    return () => {
      EventBus.off(TRAIN_HUD_UPDATE_EVENT, handleHudUpdate);
      EventBus.off(TRAIN_GAME_FINISHED_EVENT, handleFinished);
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [containerId, gameId, gameLevels, restartEnabled]);

  return (
    <FullscreenGameShell
      gameRef={gameRef}
      toolbar={<>
        <strong className="toolbar-level">SEVİYE {hud.level} / {hud.totalLevels}</strong>
        <span className="toolbar-instruction">{hud.instruction}</span>
        <span className="toolbar-score">Puan: {hud.score}</span>
        <span className="toolbar-lives" aria-label={`${hud.lives} can kaldı`}>
          {'♥ '.repeat(hud.lives)}{'♡ '.repeat(3 - hud.lives)}
        </span>
      </>}
    >
      <div className="game-container" id={containerId} role="application" aria-label={label} />
    </FullscreenGameShell>
  );
}

function initialHud(levels: TrainLevel[]): TrainHudState {
  const firstLevel = levels[0];
  return {
    level: firstLevel.id,
    totalLevels: levels.length,
    instruction: `Önce 1. vagonu cevapla • ${firstLevel.questions.length} vagondan 1. soru`,
    score: 0,
    lives: 3,
  };
}
